import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Star, X, Loader2, Package } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const cache = new Map();
const MAX = 3;

const usePhotoUrl = (equipmentId, photoId) => {
  const key = `${equipmentId}/${photoId}`;
  const [url, setUrl] = useState(cache.get(key) || "");
  useEffect(() => {
    if (!equipmentId || !photoId) return setUrl("");
    if (cache.has(key)) return setUrl(cache.get(key));
    let alive = true;
    api.get(`/equipment/${equipmentId}/photos/${photoId}`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); cache.set(key, u); if (alive) setUrl(u); })
      .catch(() => {});
    return () => { alive = false; };
  }, [equipmentId, photoId, key]);
  return url;
};

export const EquipmentThumb = ({ equipmentId, photoId, className = "w-14 h-14", testid, onClick }) => {
  const url = usePhotoUrl(equipmentId, photoId);
  return (
    <div onClick={onClick} data-testid={testid}
      className={`${className} shrink-0 bg-zinc-100 border border-zinc-200 flex items-center justify-center overflow-hidden ${onClick ? "cursor-zoom-in" : ""}`}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <Package size={18} className="text-zinc-300" />}
    </div>
  );
};

const Lightbox = ({ equipmentId, photoId, onClose }) => {
  const url = usePhotoUrl(equipmentId, photoId);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-[95vw] w-auto p-2 bg-black border-0" data-testid="equipment-photo-lightbox">
        <DialogTitle className="sr-only">Equipment photo</DialogTitle>
        {url && <img src={url} alt="" className="max-w-[90vw] max-h-[85vh] object-contain mx-auto" />}
      </DialogContent>
    </Dialog>
  );
};

export const QueuedPhotos = ({ files, setFiles }) => {
  const input = useRef(null);
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);
  const pick = (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files can be added");
    if (file.size > 5 * 1024 * 1024) return toast.error("Each photo must be 5 MB or smaller");
    setFiles([...files, file].slice(0, MAX));
  };
  return (
    <div className="flex flex-wrap gap-3" data-testid="equipment-queued-photos">
      {previews.map((u, i) => (
        <div key={u} className="relative">
          <img src={u} alt="" className={`${i === 0 ? "w-40" : "w-24"} h-32 object-cover border border-zinc-200`} />
          {i === 0 && <span className="absolute bottom-1 left-1 bg-brand text-white text-[9px] font-bold uppercase px-1.5 py-0.5">Main</span>}
          <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`equipment-queued-remove-${i}`}
            className="absolute top-1 right-1 bg-white/90 p-1 text-zinc-700 hover:text-red-600"><X size={12} /></button>
        </div>
      ))}
      {files.length < MAX && (
        <>
          <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="equipment-queued-input" />
          <button type="button" onClick={() => input.current?.click()} data-testid="equipment-queued-add"
            className="w-24 h-32 border-2 border-dashed border-zinc-300 text-zinc-500 hover:border-brand hover:text-brand flex flex-col items-center justify-center gap-1 text-[11px] font-bold transition-colors">
            <Camera size={18} /> Add photo
            <span className="font-normal text-zinc-400">{files.length}/{MAX}</span>
          </button>
        </>
      )}
    </div>
  );
};

export const EquipmentPhotos = ({ equipment: e, canManage, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const photos = e.photos || [];

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      onChange?.(r.data);
      toast.success(msg);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const upload = (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Each photo must be 5 MB or smaller");
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.post(`/equipment/${e.id}/photos`, fd), "Photo added");
  };

  if (!photos.length && !canManage) return null;
  return (
    <div className="mb-4" data-testid="equipment-photos">
      <div className="flex flex-wrap gap-3">
        {photos.map((p, i) => (
          <div key={p.id} className="relative group">
            <EquipmentThumb equipmentId={e.id} photoId={p.id} className={i === 0 ? "w-40 h-32" : "w-24 h-32"}
              testid={`equipment-photo-${p.id}`} onClick={() => setZoom(p.id)} />
            {i === 0 && <span className="absolute bottom-1 left-1 bg-brand text-white text-[9px] font-bold uppercase px-1.5 py-0.5">Main</span>}
            {canManage && (
              <div className="absolute top-1 right-1 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                {i > 0 && (
                  <button type="button" onClick={() => run(() => api.post(`/equipment/${e.id}/photos/${p.id}/main`), "Main photo set")} disabled={busy}
                    title="Make main photo" data-testid={`equipment-photo-main-${p.id}`} className="bg-white/90 p-1 text-zinc-700 hover:text-brand"><Star size={12} /></button>
                )}
                <button type="button" onClick={() => window.confirm("Remove this photo?") && run(() => api.delete(`/equipment/${e.id}/photos/${p.id}`), "Photo removed")}
                  disabled={busy} title="Remove" data-testid={`equipment-photo-remove-${p.id}`} className="bg-white/90 p-1 text-zinc-700 hover:text-red-600"><X size={12} /></button>
              </div>
            )}
          </div>
        ))}
        {canManage && photos.length < MAX && (
          <>
            <input ref={input} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="equipment-photo-input" />
            <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid="equipment-photo-add"
              className="w-24 h-32 border-2 border-dashed border-zinc-300 text-zinc-500 hover:border-brand hover:text-brand flex flex-col items-center justify-center gap-1 text-[11px] font-bold transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
              Add photo
              <span className="font-normal text-zinc-400">{photos.length}/{MAX}</span>
            </button>
          </>
        )}
      </div>
      {zoom && <Lightbox equipmentId={e.id} photoId={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
};
